import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { inboxApi, type InboxWriteResult, type InboxSendProjection } from "@/lib/api/inbox";
import { checkCommit, decodeReason } from "@/lib/api/commitSeams";
import type { PendingReply, ReplyTransport, SendDecision } from "./use-conversation-replies";
import type { NewMessageTransport } from "./new-message-box";
import { inboxWords as words } from "./inbox-copy";

function projection(raw: unknown): InboxSendProjection {
  if (!raw || typeof raw!=="object") throw new TypeError("Missing send projection");
  const value=raw as InboxSendProjection;
  if (!["confirmed","refused","unknown"].includes(value.state) || !value.operation_id || !value.course_id || !value.conversation_id)
    throw new TypeError("Invalid send projection");
  if (value.state!=="confirmed" && !decodeReason(value.reason).ids.includes(value.operation_id))
    throw new TypeError("Send reason does not name its operation");
  return value;
}
function decision(value:InboxSendProjection):SendDecision {
  if(value.state==="confirmed")return {state:"confirmed",at:new Date().toISOString()};
  return {state:value.state,sentence:value.reason?.message??words.sendUnknown};
}
function sent(value:InboxWriteResult,key:string):SendDecision {
  const answer=projection(value.commit);
  if(answer.operation_id!==key || value.operation.id!==key)throw new TypeError("Send operation changed");
  return decision(answer);
}
async function check(pending:PendingReply):Promise<SendDecision> {
  if(pending.legacy){
    // Legacy operations may have no common receipt. Only their exact operation
    // can settle them; an absent common key never releases a legacy send.
    let value=await inboxApi.operation(pending.operation);
    if(value.operation.id!==pending.operation)throw new TypeError("Legacy operation changed");
    if(value.operation.status==="unknown"){
      value=await inboxApi.reconcile(pending.operation);
      if(value.operation.id!==pending.operation)throw new TypeError("Reconciled operation changed");
    }
    return value.operation.status==="confirmed"?{state:"confirmed",at:new Date().toISOString()}
      :value.operation.status==="known_failed"?{state:"refused",sentence:words.legacyNotSent}:{state:"unknown"};
  }
  const read=async()=>{
    const answer=await checkCommit(pending.key,projection);
    if("receipt" in answer && (answer.receipt.action!=="inbox.send" || answer.receipt.result.operation_id!==pending.operation
      || answer.receipt.subject_ids.length!==1 || answer.receipt.subject_ids[0]!==pending.operation
      || answer.receipt.result.state!==answer.state))throw new TypeError("Check command changed");
    return answer;
  };
  let answer=await read();
  // Keyed transport starts only after the intent commits. Proven absence is a
  // refusal with the text kept, permitting a deliberate new command.
  if(answer.state==="not_committed")return {state:"refused",sentence:words.legacyNotSent};
  if(!("receipt" in answer))return {state:"unknown"};
  if(answer.state==="unknown"){
    const reconciled=await inboxApi.reconcile(pending.operation);
    if(reconciled.operation.id!==pending.operation)throw new TypeError("Reconciled operation changed");
    // Reconciliation observes the provider; common Check still projects the
    // original immutable command. It never sends or changes that command.
    answer=await read();
    if(!("receipt" in answer))return {state:"unknown"};
  }
  return decision(answer.receipt.result);
}
export const replyTransport:ReplyTransport={
  send:async(row,body,key)=>sent(await inboxApi.replyKeyed(row.id,body,key),key),
  check:(_row,pending)=>check(pending),
};
export const newMessageTransport:NewMessageTransport={
  send:async(request,key)=>sent(await inboxApi.createKeyed(request,key),key),check,
};

/** A terminal answer rereads the list; it never repeats mailbox refresh. */
export function useInboxTransports() {
  const client=useQueryClient();
  return useMemo(()=>{
    const settle=async(promise:Promise<SendDecision>)=>{
      const answer=await promise;
      if(answer.state!=="unknown")void client.invalidateQueries({queryKey:["inbox"]});
      return answer;
    };
    return {
      reply:{send:(row,body,key)=>settle(replyTransport.send(row,body,key)),check:(row,pending)=>settle(replyTransport.check(row,pending))} as ReplyTransport,
      compose:{send:(request,key)=>settle(newMessageTransport.send(request,key)),check:pending=>settle(newMessageTransport.check(pending))} as NewMessageTransport,
    };
  },[client]);
}
